interface TextInputProps {
  /** Optional label text displayed above the input. */
  label?: string;
  /** HTML input type (e.g. "email", "password", "text"). */
  type?: string;
  /** Placeholder text shown when the input is empty. */
  placeholder?: string;
  /** Current input value. */
  value: string;
  /** Callback fired when the input value changes. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback fired when the input loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** HTML id attribute, also used to associate the label. */
  id: string;
  /** Whether the input is required. */
  required?: boolean;
  /** Whether the input is read-only. */
  readOnly?: boolean;
  /** Whether the input should be focused on mount. */
  autoFocus?: boolean;
  /** Whether the input is disabled. */
  disabled?: boolean;
  /** Whether the input has a validation error (visual + aria only, no message rendered). */
  invalid?: boolean;
  /** ID of the element describing this input (e.g. an external error message). */
  describedBy?: string;
  /** Validation error message rendered below the input. Accepts a string or JSX (e.g. with a link). Automatically sets aria-invalid. */
  error?: React.ReactNode;
  /** id for the error paragraph rendered below the input — used by aria-describedby. */
  errorId?: string;
}

/**
 * @component TextInput
 * @description Styled text input field with an optional label. Supports various
 * input types and read-only mode. Uses a bottom-border-only style consistent
 * with the GFS login design.
 * @param props - {@link TextInputProps}
 * @returns A label (if provided) and input element.
 */
export default function TextInput({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  onBlur,
  id,
  required,
  readOnly = false,
  autoFocus = false,
  disabled = false,
  invalid = false,
  describedBy,
  error,
  errorId,
}: TextInputProps) {
  const isInvalid = invalid || !!error;
  const describedByValue = errorId || describedBy;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className={`block text-label text-muted-text mb-2${required ? " after:content-['*'] after:text-destructive after:ml-1" : ""}`}
        >
          {label}
        </label>
      )}
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={readOnly}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={!label && placeholder ? placeholder : undefined}
        aria-required={required || !readOnly}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedByValue}
        className={`w-full text-body-base bg-transparent border rounded-none px-4 py-3 outline-none transition-colors ${
          isInvalid
            ? "border-destructive"
            : "border-border-subtle focus:border-accent-green"
        } ${readOnly ? "cursor-default text-muted-text" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      />
      {error && (
        <p id={errorId} className="text-body-base text-destructive mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
